/*
 * AZP-7008 — brand and model-number facet.
 *
 * Shopify cannot filter on appliance model numbers: a part fits ~1,200 machines and
 * `list.*` metafields stop at 128 entries. The facet is driven by static JSON on the
 * Shopify CDN instead — a catalogue fetched once, then one page of models per request.
 *
 * Brands come from the same index rather than Shopify's brand filter: the store may not
 * expose one, and the names come from a different source — `GE` and `General Electric`
 * are separate brands here and would not line up.
 *
 * The collection page is left exactly as the server rendered it until a model is picked,
 * so the crawlable, CDN-cached version of every collection stays intact.
 */
(function () {
  "use strict";

  var MODEL_KEY = "az_model";
  var SORT_KEY = "sort_by";
  var PART_KEY = "az_part";
  var AVAIL_KEY = "az_stock";
  var MIN_KEY = "az_min";
  var MAX_KEY = "az_max";
  var BRAND_KEY = "az_brand";
  /* AZP-7078 — Part Type. Shopify product types carry commas and ampersands
   * ("Auger, Helix, Crusher"), and Chrome rewrites some of their escapes back on the way
   * out of `location.hash`, so the label cannot survive a round trip through the URL.
   * It travels as a slug instead, the way a brand does, and is resolved back against the
   * types the current query offers. A slug that matches nothing is dropped. */
  var TYPE_KEY = "az_type";
  var MODEL_STEP = 20;
  /* What this facet can order a filtered set by. The catalogue carries title and price;
   * The catalogue carries title, price and creation date, which covers six of the eight
   * orders the theme offers. "Featured" is a per-collection manual order, "Best selling"
   * needs sales figures, and "Most relevant" only exists inside a search query — none are
   * properties of a product, so those three stay disabled rather than pretending. */
  var SORTERS = {
    "title-ascending": function (a, b) { return a.t.localeCompare(b.t); },
    "title-descending": function (a, b) { return b.t.localeCompare(a.t); },
    "price-ascending": function (a, b) { return a.p - b.p; },
    "price-descending": function (a, b) { return b.p - a.p; },
    "created-ascending": function (a, b) { return a.d - b.d; },
    "created-descending": function (a, b) { return b.d - a.d; },
  };

  /* "Featured", "Best selling" and "Most relevant" are positions inside a set, not
   * properties of a product, so they are looked up in a captured order rather than
   * compared. `manual` is the merchant's own arrangement and barely moves; `best selling`
   * is only as fresh as the last index build; `relevance` is Shopify's own ranking for
   * the current query, captured from the handle list on a search page and therefore
   * always current. */
  var ORDER_SORTS = { manual: "m", "best-selling": "b", relevance: "r" };
  /* 250 handles per request, so this is 5,000 products — more than the store holds. A
   * query somehow larger than this would be scoped from a truncated set, which would
   * silently hide matches, so the facet steps aside instead. */
  var MAX_SCOPE_PAGES = 20;
  var RESULTS_PER_PAGE = 24;
  var CARD_SECTION = "model-result-card";
  var BRAND_STEP = 15;
  var TYPE_STEP = 10;


  /* Mirrors the theme's own `checkbox-facet` markup so this facet is indistinguishable
   * from the native ones. No `name` and no `form` attribute: these checkboxes must never
   * end up in `FacetsFilterForm`, which would send unknown params to Shopify. */
  var CHECK_SVG =
    '<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.5303 9.53033C17.8232 9.23744 17.8232 8.76256 17.5303 8.46967C17.2374 8.17678 16.7626 8.17678 16.4697 8.46967L17.5303 9.53033ZM9.99998 16L9.46965 16.5304C9.76255 16.8232 10.2374 16.8232 10.5303 16.5303L9.99998 16ZM7.53027 12.4697C7.23737 12.1768 6.7625 12.1768 6.46961 12.4697C6.17671 12.7626 6.17672 13.2374 6.46961 13.5303L7.53027 12.4697ZM16.4697 8.46967L9.46965 15.4697L10.5303 16.5303L17.5303 9.53033L16.4697 8.46967ZM6.46961 13.5303L9.46965 16.5304L10.5303 15.4697L7.53027 12.4697L6.46961 13.5303Z" /></svg>';

  function typeSlug(name) {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function optionHTML(kind, value, label, count, active) {
    var id = "az-filter-" + kind + "-" + value.replace(/[^A-Za-z0-9]+/g, "-");
    return (
      '<li class="facet-options-list-item">' +
      '<span class="checkbox-facet">' +
      '<label for="' + id + '" class="checkbox-facet-label">' +
      '<input type="checkbox" id="' + id + '" data-' + kind + '-value="' + value.replace(/"/g, "&quot;") + '"' +
      (active ? " checked" : "") + ' aria-label="' + label.replace(/"/g, "&quot;") + '">' +
      '<span class="checkbox-facet-check"></span>' +
      CHECK_SVG +
      '<span class="checkbox-facet-label-text">' + label + "</span>" +
      (count === null ? "" : '<span class="checkbox-facet-label-count">' + count + "</span>") +
      "</label></span></li>"
    );
  }

  function ModelFacet(root) {
    this.gridHTML = undefined;
    this.root = root;
    this.base = root.dataset.indexBase;
    this.version = root.dataset.indexVersion || "";
    // `/collections/all` is every product, so scoping by it would match nothing: the
    // catalogue lists real collection handles, and "all" is never one of them.
    var handle = root.dataset.collection || "";
    this.collection = handle === "all" ? "" : handle;
    // A search page has no collection to scope by; the query takes its place. `q` is read
    // from the URL rather than from Liquid so the exact string Shopify was given is the
    // one asked about, without a round trip through HTML escaping.
    this.isSearch = root.dataset.template === "search";
    this.query = this.isSearch ? this.param("q") : "";
    // `null` until a search page resolves its query: a map of the catalogue slots the
    // query matched. On a collection page it stays null and scoping runs off `c`.
    this.searchSlots = null;

    this.adopt(root);

    this.catalog = null;
    this.brands = {};
    this.brandSlugs = [];
    this.brandVisible = BRAND_STEP;
    this.models = []; // loaded so far, already collection-filtered
    this.modelVisible = MODEL_STEP;
    this.page = 0;
    this.pages = 1;
    this.brandSlug = null;
    this.model = null;
    this.results = [];
    this.resultPage = 1;
    this.originalCount = null;
    // Bumped on every render so cards from an abandoned selection cannot land in the
    // grid after a newer one started.
    this.renderToken = 0;
    this.sort = "";
    this.ready = false;
    this.orders = {};
    this.shards = {};
    this.manifest = null;
    this.handleSlot = {};
    this.stock = "";       // "in" | "out" | ""
    this.minPrice = null;
    this.maxPrice = null;
    // Part Type: the selected values, and the type of every catalogue slot the query
    // matched. Only a search page fills the map, so the facet is inert elsewhere.
    this.partTypes = [];
    this.slotType = {};
    this.typeNames = [];
    this.typeVisible = TYPE_STEP;

    this.bind();
    this.start();
  }

  /* Element lookup, kept out of the constructor so the markup and the wiring stay
   * separable — the facet is rendered by Liquid, this only fills it. */
  ModelFacet.prototype.adopt = function (root) {
    this.root = root;
    // Queried from the document, not from `root`: when `facets.js` rebuilds the sidebar it
    // moves the `.js-filter` blocks out of the `.model-facet` wrapper, so the wrapper stays
    // in the page while every control it used to hold is no longer inside it. Measured on
    // the dev store — one wrapper, one brand list, root still connected, and every
    // `root.querySelector` returning null.
    this.availabilityList = document.querySelector("[data-availability-list]");
    this.priceFrom = document.querySelector("[data-price-from]");
    this.priceTo = document.querySelector("[data-price-to]");
    this.priceHint = document.querySelector("[data-price-hint]");
    this.brandList = document.querySelector("[data-brand-list]");
    this.brandMore = document.querySelector("[data-brand-more]");
    this.typeList = document.querySelector("[data-type-list]");
    this.typeMore = document.querySelector("[data-type-more]");
    this.list = document.querySelector("[data-model-list]");
    this.more = document.querySelector("[data-model-more]");
    this.status = document.querySelector("[data-model-status]");
    this.clearButton = document.querySelector("[data-model-clear]");
    this.grid = document.getElementById("product-grid");
    // Captured once. Re-adopting after this facet has already painted would otherwise
    // record the filtered grid as the thing to restore, so clearing a filter would put
    // the filtered result back and look like nothing happened.
    if (this.gridHTML === undefined && this.grid) this.gridHTML = this.grid.innerHTML;
  };

  /* ---- data ---- */

  ModelFacet.prototype.url = function (name) {
    var url = this.base + name;
    return this.version ? url + "?v=" + encodeURIComponent(this.version) : url;
  };

  ModelFacet.prototype.fetchJSON = function (name) {
    return fetch(this.url(name))
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  };

  /* The index carries stock as of the last build, which is enough to filter the whole
   * catalogue but goes stale between builds. Shopify publishes live stock for a whole
   * collection in one request, so the collection being browsed gets corrected from it —
   * measured at 169 KB compressed and 0.6s on the dev store, fetched after the page has
   * already painted.
   *
   * Only what this endpoint knows about is overwritten: it returns the first 250 products
   * of the collection, and the index still answers for everything outside that. */
  ModelFacet.prototype.refreshStock = function () {
    if (!this.collection) return Promise.resolve();
    var self = this;
    return fetch("/collections/" + this.collection + "/products.json?limit=250")
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (payload) {
        if (!payload || !payload.products) return;
        var changed = 0;
        payload.products.forEach(function (product) {
          var slot = self.handleSlot[product.handle];
          if (slot === undefined) return;
          var live = (product.variants || []).some(function (variant) {
            return variant.available;
          })
            ? 1
            : 0;
          if (self.catalog[slot].a !== live) {
            self.catalog[slot].a = live;
            changed += 1;
          }
        });
        if (!changed) return;
        // Counts and, if the shopper is already filtering by stock, the grid itself are
        // now wrong by exactly the products that moved.
        self.renderAvailability();
        if (self.stock) self.refilter();
      })
      .catch(function () {
        // The snapshot in the index remains; a failed refresh is not a failed page.
      });
  };

  /* Which products the query matched — all of them, in Shopify's own relevance order.
   *
   * Nothing on the storefront publishes this: the results page shows one page at a time
   * and `/search/suggest.json` stops at ten. `templates/search.handles.liquid` is an
   * alternate template that runs the same search and prints nothing but the handles, so
   * one request covers 250 products and the largest query the dev store answers — 365
   * for "heating" — costs two.
   *
   * Matching has to stay Shopify's. Matching catalogue titles instead recovers a
   * fraction: 12 products against 163 for "water filter", 9 against 182 for "door
   * switch", because the query also reaches description, tags, vendor and SKU. */
  ModelFacet.prototype.scopeURL = function (page) {
    return (
      "/search?q=" +
      encodeURIComponent(this.query) +
      "&type=product&view=handles&page=" +
      page
    );
  };

  ModelFacet.prototype.fetchScopePage = function (page) {
    return fetch(this.scopeURL(page))
      .then(function (response) {
        return response.ok ? response.text() : null;
      })
      .then(function (body) {
        // `layout none` still leaves the surrounding whitespace of the template, and the
        // response is served as HTML, so this is parsed rather than handed to `.json()`.
        if (!body) return null;
        try {
          return JSON.parse(body.trim());
        } catch (error) {
          return null;
        }
      })
      .catch(function () {
        return null;
      });
  };

  ModelFacet.prototype.fetchSearchHandles = function () {
    var self = this;
    if (!this.query) return Promise.resolve(null);

    return this.fetchScopePage(1).then(function (first) {
      if (!first || !first.h) return null;
      var pages = first.pages || 1;
      if (pages > MAX_SCOPE_PAGES) return null;
      if (pages < 2) return { h: first.h, y: first.y || [] };

      var rest = [];
      for (var page = 2; page <= pages; page++) rest.push(self.fetchScopePage(page));
      return Promise.all(rest).then(function (later) {
        var handles = first.h.slice();
        var types = (first.y || []).slice();
        for (var i = 0; i < later.length; i++) {
          // A hole in the middle would silently drop matches and leave the shopper
          // filtering a set that is not the one they searched for.
          if (!later[i] || !later[i].h) return null;
          handles = handles.concat(later[i].h);
          types = types.concat(later[i].y || []);
        }
        return { h: handles, y: types };
      });
    });
  };

  /* Handles become catalogue slots, and their order becomes the relevance ranking the
   * sort reads — the same mechanism "Featured" and "Best selling" use on a collection. */
  ModelFacet.prototype.applySearchScope = function (scope) {
    var self = this;
    var handles = scope.h;
    var types = scope.y || [];
    var slots = {};
    var order = [];
    handles.forEach(function (handle, position) {
      var slot = self.handleSlot[handle];
      if (slot === undefined || slots[slot]) return;
      slots[slot] = true;
      order.push(slot);
      // A product with no type has nothing to offer this facet; leaving it unmapped keeps
      // it out of the option list, and out of every set the facet filters.
      var type = types[position];
      if (type) self.slotType[slot] = type;
    });
    this.searchSlots = slots;
    this.orders.r = order;
    this.collectTypes();
  };

  /* The Part Type options: every type present in the matched set, A to Z.
   *
   * Product type is not in the index on the CDN and this shop's Search & Discovery offers
   * no Product type filter, so the values come from the same handle list that already
   * scopes the search — `templates/search.handles.liquid` prints them alongside. */
  ModelFacet.prototype.collectTypes = function () {
    var self = this;
    var seen = {};
    Object.keys(this.searchSlots || {}).forEach(function (slot) {
      var type = self.slotType[slot];
      if (type) seen[type] = true;
    });
    this.typeNames = Object.keys(seen).sort(function (a, b) {
      return a.localeCompare(b);
    });
  };

  /* The brands worth offering: those with at least one product in the set on screen.
   *
   * A collection is a fixed set, so `brands[slug].c` already answers this. A search
   * result set is not, so the answer comes from the brands written onto each catalogue
   * entry by the index builder. */
  ModelFacet.prototype.scopeBrands = function () {
    var self = this;
    var slugs;

    if (this.searchSlots) {
      var present = {};
      var known = false;
      Object.keys(this.searchSlots).forEach(function (slot) {
        var entry = self.catalog[slot];
        if (!entry || !entry.b) return;
        known = true;
        entry.b.forEach(function (slug) {
          if (self.brands[slug]) present[slug] = true;
        });
      });
      // An index built before brands were written onto the catalogue cannot answer this.
      // Offering every brand is wrong in a smaller way than offering none, and the model
      // list underneath is scoped either way.
      slugs = known ? Object.keys(present) : Object.keys(this.brands);
    } else {
      var collection = this.collection;
      slugs = Object.keys(this.brands).filter(function (slug) {
        var brand = self.brands[slug];
        return !collection || !brand.c || brand.c.indexOf(collection) !== -1;
      });
    }

    return slugs.sort(function (a, b) {
      return self.brands[a].b.localeCompare(self.brands[b].b);
    });
  };

  ModelFacet.prototype.start = function () {
    var self = this;
    this.setStatus("Loading…");

    Promise.all([
      this.fetchJSON("catalog.json"),
      this.fetchJSON("brands.json"),
      this.fetchJSON("orders.json"),
      // Runs alongside the index rather than after it: the query is answered by Shopify
      // and the catalogue by the CDN, so neither waits for the other.
      this.fetchSearchHandles(),
    ]).then(
      function (results) {
        self.orders = (results[2] || {})[self.collection] || {};
        if (!results[0] || !results[1]) {
          self.setStatus("Model list unavailable.");
          return;
        }
        self.catalog = results[0];
        self.brands = results[1];
        self.catalog.forEach(function (entry, index) {
          self.handleSlot[entry.h] = index;
        });
        if (self.query) {
          if (!results[3]) {
            // Without the matched set these facets would filter the whole catalogue and
            // quietly throw the query away, which is worse than not offering them.
            self.setStatus("Filters are unavailable for this search.");
            return;
          }
          self.applySearchScope(results[3]);
        }
        self.ready = true;
        self.brandSlugs = self.scopeBrands();
        self.renderBrands();
        self.renderTypes();
        self.setStatus("Pick a brand to see the models it covers.");
        self.restore();
        // After restore, never before: correcting stock can re-run the filter, and doing
        // that while the URL state is still being applied would race it.
        self.refreshStock();
      }
    );
  };

  /* A model earns a place only if one of its parts sits in the collection being
   * browsed; otherwise picking it would land on an empty grid. */
  /* Every facet narrows the same set, which is the whole point of replacing the native
   * ones: a native filter runs on the server and re-renders, then this facet paints over
   * the result, so the two could never combine. */
  ModelFacet.prototype.applyFacets = function (indexes) {
    var self = this;
    return indexes.filter(function (index) {
      var entry = self.catalog[index];
      if (!entry) return false;
      // On a search page the query is a filter like any other, and it has to narrow every
      // path — including a model or part number resolved straight from the search box,
      // which reaches the catalogue without passing through `inScope`.
      if (self.searchSlots && self.searchSlots[index] !== true) return false;
      if (self.partTypes.length && self.partTypes.indexOf(self.slotType[index]) === -1) {
        return false;
      }
      if (self.stock === "in" && !entry.a) return false;
      if (self.stock === "out" && entry.a) return false;
      if (self.minPrice !== null && entry.p < self.minPrice) return false;
      if (self.maxPrice !== null && entry.p > self.maxPrice) return false;
      return true;
    });
  };

  /* The set the shopper is standing in: a collection on a collection page, the products a
   * query matched on a search page. Both narrow the same way, which is why the search
   * page needed no second code path — only a second way of answering this. */
  ModelFacet.prototype.inScope = function (indexes) {
    var self = this;
    if (this.searchSlots) {
      return indexes.filter(function (index) {
        return self.searchSlots[index] === true;
      });
    }
    if (!this.collection) return indexes;
    return indexes.filter(function (index) {
      var entry = self.catalog[index];
      return entry && entry.c && entry.c.indexOf(self.collection) !== -1;
    });
  };

  ModelFacet.prototype.loadNextPage = function () {
    var self = this;
    if (this.page >= this.pages) return Promise.resolve();

    return this.fetchJSON("brand-" + this.brandSlug + "-" + (this.page + 1) + ".json").then(
      function (page) {
        if (!page) {
          self.pages = self.page;
          self.renderModels();
          return;
        }
        self.page = page.page;
        self.pages = page.pages;

        page.m.forEach(function (entry) {
          var hits = self.inScope(entry.p);
          if (hits.length) self.models.push({ n: entry.n, p: hits });
        });

        // Collection scoping can empty a whole page, so keep pulling until there is
        // enough to show or the brand runs out.
        if (self.models.length < self.modelVisible && self.page < self.pages) {
          return self.loadNextPage();
        }
        self.renderModels();
        if (!self.model) self.showBrandResults();
      }
    );
  };

  /* Every product the brand covers inside this collection, deduplicated. Picking a
   * brand is already a filter — waiting for a model before touching the grid would
   * leave the shopper looking at the unfiltered collection. */
  ModelFacet.prototype.brandResults = function () {
    var seen = {};
    var indexes = [];
    this.models.forEach(function (entry) {
      entry.p.forEach(function (index) {
        if (seen[index]) return;
        seen[index] = true;
        indexes.push(index);
      });
    });
    return indexes.sort(function (a, b) {
      return a - b;
    });
  };


  ModelFacet.prototype.renderAvailability = function () {
    if (!this.availabilityList) return;
    var self = this;
    var counts = { in: 0, out: 0 };
    this.catalog.forEach(function (entry) {
      if (!self.collection || (entry.c || []).indexOf(self.collection) !== -1) {
        counts[entry.a ? "in" : "out"] += 1;
      }
    });
    this.availabilityList.innerHTML =
      optionHTML("stock", "in", "In stock", counts.in, this.stock === "in") +
      optionHTML("stock", "out", "Out of stock", counts.out, this.stock === "out");
  };

  ModelFacet.prototype.renderPriceHint = function () {
    if (!this.priceHint) return;
    var self = this;
    var prices = this.catalog
      .filter(function (entry) {
        return !self.collection || (entry.c || []).indexOf(self.collection) !== -1;
      })
      .map(function (entry) { return entry.p; });
    if (!prices.length) return;
    this.priceHint.textContent =
      "The highest price is $" + Math.max.apply(null, prices).toFixed(2);
    if (this.priceFrom && this.minPrice !== null) this.priceFrom.value = this.minPrice;
    if (this.priceTo && this.maxPrice !== null) this.priceTo.value = this.maxPrice;
  };

  ModelFacet.prototype.renderBrands = function () {
    if (!this.brandList) return;
    var self = this;
    var shown = this.brandSlugs.slice(0, this.brandVisible);

    this.brandList.innerHTML = shown
      .map(function (slug) {
        return optionHTML("brand", slug, self.brands[slug].b, null, slug === self.brandSlug);
      })
      .join("");

    var remaining = this.brandSlugs.length - shown.length;
    if (this.brandMore) this.brandMore.hidden = remaining <= 0;
    if (this.brandMore) this.brandMore.textContent = "Show " + Math.min(remaining, BRAND_STEP) + " more brands";
  };

  /* Slugs back to labels. The labels are what `applyFacets` compares against, and only
   * the types this query offers can be selected, so an unknown slug is simply dropped. */
  ModelFacet.prototype.typesFromParam = function (value) {
    if (!value) return [];
    var wanted = {};
    value.split(",").forEach(function (slug) {
      if (slug) wanted[slug] = true;
    });
    return this.typeNames.filter(function (name) {
      return wanted[typeSlug(name)] === true;
    });
  };

  /* Several types can be on at once — they are alternatives, not a refinement of each
   * other, and the design draws checkboxes rather than a single-choice list. */
  ModelFacet.prototype.renderTypes = function () {
    if (!this.typeList) return;
    var self = this;
    // A type restored from the URL can sit past the tenth, and a checkbox the shopper
    // cannot see is a filter they cannot turn off. The window stretches to reach it.
    var visible = this.typeVisible;
    this.partTypes.forEach(function (name) {
      var at = self.typeNames.indexOf(name);
      if (at >= visible) visible = at + 1;
    });
    var shown = this.typeNames.slice(0, visible);

    this.typeList.innerHTML = shown
      .map(function (name) {
        return optionHTML("type", name, name, null, self.partTypes.indexOf(name) !== -1);
      })
      .join("");

    var remaining = this.typeNames.length - shown.length;
    if (!this.typeMore) return;
    this.typeMore.hidden = remaining <= 0;
    this.typeMore.textContent =
      "Show " + Math.min(remaining, TYPE_STEP) + " more part types";
  };

  ModelFacet.prototype.renderModels = function () {
    if (!this.list) return;
    var shown = this.models.slice(0, this.modelVisible);

    if (!shown.length) {
      if (this.list) this.list.innerHTML = "";
      if (this.more) this.more.hidden = true;
      if (this.brandSlug) this.setStatus("No model for this brand in this collection.");
      return;
    }

    var self = this;
    this.list.innerHTML = shown
      .map(function (entry) {
        return optionHTML("model", entry.n, entry.n, entry.p.length, entry.n === self.model);
      })
      .join("");

    // Count only what this facet can actually offer: models whose parts are in the
    // collection being browsed. The brand's store-wide total would promise entries that
    // no amount of "load more" can reveal here.
    var matched = this.models.length;
    var allLoaded = this.page >= this.pages;
    var label;
    if (!allLoaded) {
      label = shown.length + " of " + matched + "+ models";
    } else if (shown.length < matched) {
      label = shown.length + " of " + matched + " models";
    } else {
      label = matched + (matched === 1 ? " model" : " models");
    }
    this.setStatus(label);
    if (this.more) this.more.hidden = this.models.length <= this.modelVisible && this.page >= this.pages;
  };

  ModelFacet.prototype.setStatus = function (message) {
    if (!this.status) return;
    this.status.textContent = message || "";
    this.status.hidden = !message;
  };

  /* ---- selection ---- */

  ModelFacet.prototype.selectBrand = function (slug) {
    this.brandSlug = slug;
    // A model belongs to the brand it was picked under; carrying it across would leave
    // the grid showing the previous brand's result and skip the new brand's own.
    this.model = null;
    this.results = [];
    if (this.clearButton) this.clearButton.hidden = true;
    this.models = [];
    this.page = 0;
    this.pages = this.brands[slug] ? this.brands[slug].pages : 1;
    this.modelVisible = MODEL_STEP;
    this.renderBrands();
    this.writeState();
    this.setStatus("Loading models…");
    return this.loadNextPage();
  };

  ModelFacet.prototype.clearBrand = function () {
    this.brandSlug = null;
    this.models = [];
    this.page = 0;
    if (this.list) this.list.innerHTML = "";
    if (this.more) this.more.hidden = true;
    this.renderBrands();
    this.model = null;
    this.results = [];
    if (this.clearButton) this.clearButton.hidden = true;
    this.writeState();
    // Not `restoreGrid()`: price and availability may still be on, and putting the whole
    // collection back would silently drop them.
    this.applyToNativeGrid();
    if (!this.hasOwnFilter()) this.setStatus("Pick a brand to see the models it covers.");
  };


  /* A model number can arrive without a brand — the search box sends one straight here —
   * so it is resolved from the prefix shards rather than from a brand's pages. The shards
   * chain: a prefix that grew too large is replaced by a stub naming a deeper one, because
   * every Kenmore model starts `110`. */
  /* Models are filed in one sorted sequence cut into shards of roughly equal size;
   * `models-manifest.json` holds the first key of each. Finding the right shard is a
   * binary search over ~48 strings, so a direct model lookup costs the manifest once and
   * one shard thereafter.
   *
   * Separators are ignored when picking the shard and nowhere else: the models inside
   * stay verbatim, because 617 of them collide once separators go. */
  function sortKey(text) {
    return String(text || "").toUpperCase().replace(/[^A-Z0-9]+/g, "");
  }

  ModelFacet.prototype.shard = function (query) {
    var self = this;
    var manifest = this.manifest;
    if (!manifest) {
      return this.fetchJSON("models-manifest.json").then(function (data) {
        self.manifest = data || { b: [] };
        return self.shard(query);
      });
    }

    var bounds = manifest.b || [];
    if (!bounds.length) return Promise.resolve(null);
    var key = sortKey(query);
    var low = 0;
    var high = bounds.length - 1;
    while (low < high) {
      var mid = Math.ceil((low + high) / 2);
      if (bounds[mid] <= key) low = mid;
      else high = mid - 1;
    }
    var name = "models-p" + (low < 10 ? "0" + low : low);

    if (this.shards[name] !== undefined) return Promise.resolve(this.shards[name]);
    return this.fetchJSON(name + ".json").then(function (data) {
      self.shards[name] = data;
      return data;
    });
  };

  ModelFacet.prototype.showModelDirect = function (model) {
    var self = this;
    this.shard(model).then(function (shard) {
      var hits = shard && shard.m ? shard.m[model] : null;
      if (!hits || !hits.length) {
        self.setStatus("No part fits " + model + ".");
        return;
      }
      // The shard indexes its own product list, not the catalogue, so the handles are
      // mapped back through it.
      var slots = hits
        .map(function (index) {
          return self.handleSlot[shard.p[index].h];
        })
        .filter(function (slot) {
          return slot !== undefined;
        });
      if (!slots.length) {
        self.setStatus("No part fits " + model + ".");
        return;
      }
      self.model = model;
      self.results = self.finalise(slots);
      self.resultPage = 1;
      self.renderResults(model);
      if (self.clearButton) self.clearButton.hidden = false;
    });
  };

  ModelFacet.prototype.showPartNumber = function (number) {
    var self = this;
    this.fetchJSON("partnumbers.json").then(function (data) {
      var slots = data && data.n ? data.n[number] : null;
      if (!slots || !slots.length) {
        self.setStatus("No part matches " + number + ".");
        return;
      }
      self.results = self.finalise(slots);
      self.resultPage = 1;
      self.renderResults("part " + number);
      if (self.clearButton) self.clearButton.hidden = false;
    });
  };

  ModelFacet.prototype.showBrandResults = function () {
    if (!this.grid || !this.brandSlug) return;
    this.results = this.finalise(this.brandResults());
    this.resultPage = 1;
    if (!this.results.length) {
      // An empty result is an answer, not a reason to show everything.
      if (this.grid) this.grid.innerHTML = "";
      this.renderCount(0);
      this.setThemePagination(false);
      this.setStatus("No parts match this selection.");
      return;
    }
    this.renderResults(this.brands[this.brandSlug].b);
  };

  ModelFacet.prototype.restoreGrid = function () {
    this.renderToken++;
    if (this.grid) this.grid.innerHTML = this.gridHTML;
    var pagination = document.querySelector("[data-model-pagination]");
    if (pagination) pagination.remove();
    this.restoreCount();
    this.setThemePagination(true);
  };

  ModelFacet.prototype.selectModel = function (model) {
    var entry = null;
    for (var i = 0; i < this.models.length; i++) {
      if (this.models[i].n === model) {
        entry = this.models[i];
        break;
      }
    }
    if (!entry || !this.grid) return;

    this.model = model;
    this.results = this.finalise(entry.p);
    this.resultPage = 1;
    this.writeState();
    if (this.clearButton) this.clearButton.hidden = false;
    this.renderModels();
    this.renderResults();
  };

  /* Cards come from the theme, not from a template kept here: a copy of `card-product`
   * would lose the image, badges, stock line and add-to-cart the moment either side
   * changed. One request per visible card, and only for the page being shown. */
  ModelFacet.prototype.renderResults = function (subject) {
    var self = this;
    var total = this.results.length;
    var pages = Math.max(1, Math.ceil(total / RESULTS_PER_PAGE));
    var page = Math.min(Math.max(this.resultPage, 1), pages);
    this.resultPage = page;

    var slice = this.results.slice((page - 1) * RESULTS_PER_PAGE, page * RESULTS_PER_PAGE);
    var token = ++this.renderToken;
    this.setStatus(total + " part(s) for " + (subject || this.model));
    this.grid.innerHTML = "";
    this.renderCount(total);

    slice.forEach(function (index, position) {
      var product = self.catalog[index];
      var placeholder = document.createElement("li");
      placeholder.className = "grid-item model-facet-loading";
      self.grid.appendChild(placeholder);

      fetch("/products/" + product.h + "?section_id=" + CARD_SECTION)
        .then(function (response) {
          return response.ok ? response.text() : null;
        })
        .then(function (html) {
          if (token !== self.renderToken) return; // a newer selection took over
          if (!html) {
            placeholder.remove();
            return;
          }
          var holder = document.createElement("div");
          holder.innerHTML = html.trim();
          var card = holder.querySelector("[data-model-result-card]") || holder.firstElementChild;
          if (card) placeholder.replaceWith(card);
          else placeholder.remove();
        })
        .catch(function () {
          placeholder.remove();
        });
      return position;
    });

    this.renderPagination(page, pages);
  };

  /* The theme already prints the result count in `#ProductCount`; rewriting it keeps the
   * filtered view in the same place and the same type as an unfiltered one, and the
   * original text is put back when the filter clears. */
  ModelFacet.prototype.renderCount = function (total) {
    var node = document.getElementById("ProductCount");
    if (!node) return;
    if (this.originalCount === null) this.originalCount = node.textContent;
    // AZP-7078 — the search page counts "Showing N Parts", so a filtered count has to
    // read the same way; a collection keeps the wording it had.
    node.textContent = this.isSearch
      ? "Showing " + total + (total === 1 ? " Part" : " Parts")
      : total + (total === 1 ? " Product" : " Products");
  };

  ModelFacet.prototype.restoreCount = function () {
    var node = document.getElementById("ProductCount");
    if (node && this.originalCount !== null) node.textContent = this.originalCount;
  };

  /* The server-rendered page keeps its own pagination for the unfiltered collection —
   * nine pages of dryer parts stay on screen next to a single filtered result unless it
   * is put away while the filter is on. */
  ModelFacet.prototype.setThemePagination = function (visible) {
    document.querySelectorAll(".pagination:not([data-model-pagination])").forEach(
      function (node) {
        node.hidden = !visible;
      }
    );
  };

  ModelFacet.prototype.renderPagination = function (page, pages) {
    var existing = document.querySelector("[data-model-pagination]");
    if (existing) existing.remove();
    this.setThemePagination(false);
    if (pages <= 1) return;

    var wrap = document.createElement("div");
    wrap.className = "pagination";
    wrap.setAttribute("data-model-pagination", "");

    var parts = [];
    if (page > 1) parts.push({ label: "‹", page: page - 1, cls: "page-number page-number-prev" });
    for (var n = 1; n <= pages; n++) {
      if (pages > 7 && n > 2 && n < pages - 1 && Math.abs(n - page) > 1) {
        if (parts[parts.length - 1] && parts[parts.length - 1].gap) continue;
        parts.push({ label: "…", gap: true, cls: "page-number page-number-gap" });
        continue;
      }
      parts.push({
        label: String(n),
        page: n,
        cls: n === page ? "page-number page-number-active" : "page-number",
      });
    }
    if (page < pages) parts.push({ label: "›", page: page + 1, cls: "page-number page-number-next" });

    var self = this;
    parts.forEach(function (part) {
      var node = document.createElement(part.page ? "button" : "span");
      node.className = part.cls;
      node.textContent = part.label;
      if (part.page) {
        node.type = "button";
        node.addEventListener("click", function () {
          self.resultPage = part.page;
          self.renderResults();
          self.grid.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
      wrap.appendChild(node);
    });

    this.grid.parentNode.insertBefore(wrap, this.grid.nextSibling);
  };

  ModelFacet.prototype.clearModel = function () {
    this.model = null;
    this.results = [];
    if (this.clearButton) this.clearButton.hidden = true;
    this.writeState();
    this.renderModels();
    // Fall back to whatever is still selected — the brand, or price and availability on
    // their own — rather than all the way to the unfiltered collection.
    if (this.brandSlug) this.showBrandResults();
    else this.applyToNativeGrid();
  };


  /* ---- URL state ---- */

  /* State rides in the URL so a reload or a shared link restores it. A query param would
   * also survive the theme's re-render, but it navigates; the facet must feel like the
   * native ones, which never reload the page. */
  ModelFacet.prototype.param = function (key) {
    var search = new URLSearchParams(window.location.search).get(key);
    if (search) return search;
    var match = window.location.hash.match(new RegExp("[#&]" + key + "=([^&]+)"));
    return match ? decodeURIComponent(match[1]) : null;
  };

  /* Slugs are [a-z0-9-] only, so a comma joins them unambiguously and nothing needs
   * escaping. */
  ModelFacet.prototype.encodedTypes = function () {
    return this.partTypes.map(typeSlug).join(",");
  };

  /* Written to both the fragment and the theme's hidden inputs: the fragment survives a
   * reload, the inputs make `facets.js` carry the state through its next FormData read
   * when a sort or a native filter fires. */
  ModelFacet.prototype.writeState = function () {
    var parts = [];
    if (this.brandSlug) parts.push(BRAND_KEY + "=" + encodeURIComponent(this.brandSlug));
    if (this.model) parts.push(MODEL_KEY + "=" + encodeURIComponent(this.model));
    if (this.sort) parts.push(SORT_KEY + "=" + encodeURIComponent(this.sort));
    if (this.partTypes.length) parts.push(TYPE_KEY + "=" + this.encodedTypes());
    if (this.stock) parts.push(AVAIL_KEY + "=" + this.stock);
    if (this.minPrice !== null) parts.push(MIN_KEY + "=" + this.minPrice);
    if (this.maxPrice !== null) parts.push(MAX_KEY + "=" + this.maxPrice);
    var url = window.location.pathname + window.location.search;
    history.replaceState(null, "", parts.length ? url + "#" + parts.join("&") : url);

    this.updateSortOptions();

    var values = {};
    values[BRAND_KEY] = this.brandSlug || "";
    values[MODEL_KEY] = this.model || "";
    values[TYPE_KEY] = this.partTypes.length ? this.encodedTypes() : "";
    values[AVAIL_KEY] = this.stock || "";
    values[MIN_KEY] = this.minPrice === null ? "" : this.minPrice;
    values[MAX_KEY] = this.maxPrice === null ? "" : this.maxPrice;
    Object.keys(values).forEach(function (name) {
      var input = document.querySelector('input[name="' + name + '"]');
      if (input) input.value = values[name];
    });
  };

  /* Applies whatever state the URL carries — on first load, and again after the theme
   * re-renders the sidebar, where the facet markup comes back empty. */
  /* Re-runs whichever view is active under the new facet values, without refetching. */
  /* Wraps a step so a thrown error lands in the facet's own status line. A silent throw
   * here leaves every list empty and reads as missing data — which cost most of a day
   * once already. */
  ModelFacet.prototype.guard = function (label, fn) {
    try {
      return fn();
    } catch (error) {
      if (this.status) {
        this.status.hidden = false;
        this.status.textContent = label + " failed: " + (error && error.message);
      }
      // Logged, shown, and swallowed: a failed facet must not take the rest of the page
      // with it, and re-throwing left every list frozen with no sign of why.
      console.error("[model-facet] " + label, error);
      return null;
    }
  };

  /* Price and availability are filters in their own right. Treating only brand and model
   * as "filtering" sent a sort change down the native path — reloading the page and
   * handing Shopify a sort over the whole collection — and left a URL carrying only
   * `az_min` or `az_stock` doing nothing at all on load. */
  ModelFacet.prototype.hasOwnFilter = function () {
    return Boolean(
      this.brandSlug ||
        this.model ||
        this.param(PART_KEY) ||
        this.partTypes.length ||
        this.stock ||
        this.minPrice !== null ||
        this.maxPrice !== null
    );
  };

  ModelFacet.prototype.refilter = function () {
    var self = this;
    return this.guard("Filter", function () {
      return self.refilterInner();
    });
  };

  ModelFacet.prototype.refilterInner = function () {
    this.renderAvailability();
    this.renderTypes();
    this.writeState();
    if (this.model && this.models.length) this.selectModel(this.model);
    else if (this.model) this.showModelDirect(this.model);
    else if (this.brandSlug) this.showBrandResults();
    else if (this.param(PART_KEY)) this.showPartNumber(this.param(PART_KEY));
    else this.applyToNativeGrid();
  };

  /* With no brand, model or part chosen there is no result set of our own, so the
   * page's own grid is narrowed instead of replaced. */
  ModelFacet.prototype.applyToNativeGrid = function () {
    var filtering =
      this.partTypes.length ||
      this.stock ||
      this.minPrice !== null ||
      this.maxPrice !== null;
    if (!filtering) return this.restoreGrid();

    var all = this.catalog.map(function (_, index) { return index; });
    this.results = this.finalise(this.inScope(all));
    this.resultPage = 1;
    this.renderResults("this selection");
  };

  ModelFacet.prototype.restore = function () {
    var self = this;

    // A part number arrives from the search box, which sends number queries here rather
    // than to `/search` — Shopify cannot index these, so its results page would be empty
    // and, having found nothing, would render no sidebar or pagination either.
    this.stock = this.param(AVAIL_KEY) || "";
    this.partTypes = this.typesFromParam(this.param(TYPE_KEY));
    var min = parseFloat(this.param(MIN_KEY));
    var max = parseFloat(this.param(MAX_KEY));
    this.minPrice = isNaN(min) ? null : min;
    this.maxPrice = isNaN(max) ? null : max;
    this.renderAvailability();
    this.renderTypes();
    this.renderPriceHint();

    var partNumber = this.param(PART_KEY);
    if (partNumber) return this.showPartNumber(partNumber);

    var brand = this.param(BRAND_KEY);
    if (!brand || !this.brands[brand]) {
      // A model with no brand comes from the search box; resolve it on its own.
      var loneModel = this.param(MODEL_KEY);
      if (loneModel) return this.showModelDirect(loneModel);
      this.writeState();
      // Price or availability on their own still narrow the page's own grid.
      if (this.hasOwnFilter()) this.applyToNativeGrid();
      return;
    }
    if (brand === this.brandSlug && this.models.length) {
      this.renderBrands();
      this.renderModels();
      if (this.model) this.selectModel(this.model);
      else this.showBrandResults();
      return;
    }

    var model = this.param(MODEL_KEY);
    var loading = this.selectBrand(brand);
    if (!loading || !loading.then || !model) return;

    loading.then(function () {
      // The model may sit past the first page, so keep loading until it turns up.
      (function seek() {
        var found = self.models.some(function (entry) {
          return entry.n === model;
        });
        if (found) return self.selectModel(model);
        if (self.page >= self.pages) return;
        self.loadNextPage().then(seek);
      })();
    });
  };

  /* ---- events ---- */

  /* The theme's sort select posts through `FacetsFilterForm`, which makes `facets.js`
   * re-render the sidebar and destroy this facet. Detaching it from the form and handling
   * the change here keeps the selection intact; with no facet active the choice is passed
   * straight back to Shopify, which sorts far better than the browser can. */
  ModelFacet.prototype.takeOverSort = function () {
    var self = this;
    var select = document.getElementById("collection-sort-order");
    if (!select) return;

    select.removeAttribute("form");
    // Removing the form attribute stops the theme submitting it, but `facets.js` also
    // listens for `input` on anything inside `.collection-actions-wrapper` — which is
    // where this select lives — and re-renders the whole sidebar, replacing every facet
    // node this instance holds. That is why sorting first worked (it navigated away) and
    // sorting after a filter did not: the nodes were dead by the time they were clicked.
    ["input", "change"].forEach(function (type) {
      select.addEventListener(type, function (event) {
        event.stopPropagation();
      });
    });
    this.sort = this.param(SORT_KEY) || select.value || "";
    if (this.sort && select.value !== this.sort) select.value = this.sort;

    select.addEventListener("change", function () {
      self.sort = select.value;
      if (!self.ready) return; // the index is still loading; the URL already holds the state
      if (!self.hasOwnFilter()) {
        // Nothing of ours is filtering: let Shopify do it, server-side. Assigning to
        // `location.search` would drop the fragment, so the whole URL is rebuilt.
        var params = new URLSearchParams(window.location.search);
        params.set("sort_by", select.value);
        window.location.href =
          window.location.pathname + "?" + params.toString() + window.location.hash;
        return;
      }
      self.refilter();
    });
  };

  /* While this facet is filtering, only the orders the catalogue can actually produce stay
   * selectable. Leaving "Best selling" clickable would change nothing when picked, which
   * reads as a broken control rather than an unsupported one. */
  ModelFacet.prototype.updateSortOptions = function () {
    var self = this;
    var select = document.getElementById("collection-sort-order");
    if (!select) return;
    var filtering = this.hasOwnFilter();

    Array.prototype.forEach.call(select.options, function (option) {
      var orderKey = ORDER_SORTS[option.value];
      var supported =
        !filtering ||
        Object.prototype.hasOwnProperty.call(SORTERS, option.value) ||
        Boolean(orderKey && self.orders[orderKey]);
      option.disabled = !supported;
      option.textContent = option.textContent.replace(/ \(unavailable while filtering\)$/, "");
      if (!supported) option.textContent += " (unavailable while filtering)";
    });

    if (filtering && select.selectedOptions[0] && select.selectedOptions[0].disabled) {
      // Only an order the page happened to load with, never one just chosen — the change
      // handler always selects an enabled option.
      this.sort = "";
      select.value = "title-ascending";
    }
  };

  ModelFacet.prototype.finalise = function (indexes) {
    return this.sortResults(this.applyFacets(indexes));
  };

  ModelFacet.prototype.sortResults = function (indexes) {
    var orderKey = ORDER_SORTS[this.sort];
    if (orderKey && this.orders[orderKey]) {
      var rank = {};
      this.orders[orderKey].forEach(function (slot, position) {
        rank[slot] = position;
      });
      // Anything the captured order does not mention goes last rather than to the front,
      // which is what a missing key would otherwise do.
      return indexes.slice().sort(function (a, b) {
        var ra = rank[a] === undefined ? Infinity : rank[a];
        var rb = rank[b] === undefined ? Infinity : rank[b];
        return ra - rb;
      });
    }

    var compare = SORTERS[this.sort];
    if (!compare) return indexes;
    var catalog = this.catalog;
    return indexes.slice().sort(function (a, b) {
      return compare(catalog[a], catalog[b]);
    });
  };

  ModelFacet.prototype.bind = function () {
    this.bindFacet();
    this.takeOverSort();
    this.hookThemeRender();

    var self = this;
    window.addEventListener("popstate", function () {
      self.restore();
    });
  };

  /* `facets.js` replaces the whole sidebar (`renderFilters`) and the whole grid
   * (`renderProductGridContainer`) on every sort or native filter change. The facet
   * markup lives inside the sidebar, so it comes back empty and detached from this
   * instance. Wrapping the two functions is exact; watching the DOM was not, because a
   * theme swap looks identical to this facet's own writes. */
  ModelFacet.prototype.hookThemeRender = function () {
    var self = this;
    if (typeof FacetFiltersForm === "undefined" || FacetFiltersForm.azModelFacetHooked) return;
    FacetFiltersForm.azModelFacetHooked = true;

    ["renderFilters", "renderProductGridContainer"].forEach(function (name) {
      var original = FacetFiltersForm[name];
      if (typeof original !== "function") return;
      FacetFiltersForm[name] = function () {
        var result = original.apply(this, arguments);
        setTimeout(function () {
          var root = document.querySelector("[data-model-facet]");
          if (!root) return;
          self.adopt(root);
          self.bindFacet();
          self.restore();
        }, 0);
        return result;
      };
    });
  };

  ModelFacet.prototype.bindFacet = function () {
    var self = this;

    if (this.availabilityList) {
      this.availabilityList.addEventListener("input", function (event) {
      event.stopPropagation();
    });

    this.availabilityList.addEventListener("click", function (event) {
        var item = event.target.closest(".facet-options-list-item");
        var option = item && item.querySelector("[data-stock-value]");
        if (!option) return;
        var value = option.dataset.stockValue;
        self.guard("Availability", function () {
          self.stock = self.stock === value ? "" : value;
          self.refilterInner();
        });
      });
    }

    if (this.typeList) {
      this.typeList.addEventListener("input", function (event) {
        event.stopPropagation();
      });

      this.typeList.addEventListener("click", function (event) {
        var item = event.target.closest(".facet-options-list-item");
        var option = item && item.querySelector("[data-type-value]");
        if (!option) return;
        var value = option.dataset.typeValue;
        self.guard("Part Type", function () {
          var at = self.partTypes.indexOf(value);
          if (at === -1) self.partTypes.push(value);
          else self.partTypes.splice(at, 1);
          self.refilterInner();
        });
      });
    }

    if (this.typeMore) {
      this.typeMore.addEventListener("click", function (event) {
        event.preventDefault();
        self.typeVisible += TYPE_STEP;
        self.renderTypes();
      });
    }

    [this.priceFrom, this.priceTo].forEach(function (input) {
      if (!input) return;
      var timer = null;
      // `facets.js` listens for `input` on anything inside `.facet` and submits the
      // theme's filter form, which re-renders the section and replaces these inputs with
      // empty ones — the typed digits vanish mid-keystroke. Stopping the event here keeps
      // it from ever reaching that listener.
      input.addEventListener("input", function (event) {
        event.stopPropagation();
        clearTimeout(timer);
        timer = setTimeout(function () {
          var from = parseFloat(self.priceFrom && self.priceFrom.value);
          var to = parseFloat(self.priceTo && self.priceTo.value);
          self.minPrice = isNaN(from) ? null : from;
          self.maxPrice = isNaN(to) ? null : to;
          self.refilter();
        }, 300);
      });
    });

    this.brandList.addEventListener("input", function (event) {
      event.stopPropagation();
    });

    this.brandList.addEventListener("click", function (event) {
      var item = event.target.closest(".facet-options-list-item");
      var option = item && item.querySelector("[data-brand-value]");
      if (!option) return;
      var slug = option.dataset.brandValue;
      self.guard("Brand", function () {
        if (slug === self.brandSlug) self.clearBrand();
        else self.selectBrand(slug);
      });
    });

    this.brandMore.addEventListener("click", function (event) {
      event.preventDefault();
      self.brandVisible += BRAND_STEP;
      self.renderBrands();
    });

    this.list.addEventListener("input", function (event) {
      event.stopPropagation();
    });

    this.list.addEventListener("click", function (event) {
      var item = event.target.closest(".facet-options-list-item");
      var option = item && item.querySelector("[data-model-value]");
      if (!option) return;
      var model = option.dataset.modelValue;
      self.guard("Model", function () {
        if (model === self.model) self.clearModel();
        else self.selectModel(model);
      });
    });

    this.more.addEventListener("click", function (event) {
      event.preventDefault();
      self.modelVisible += MODEL_STEP;
      if (self.models.length <= self.modelVisible && self.page < self.pages) {
        self.loadNextPage();
      } else {
        self.renderModels();
      }
    });

    this.clearButton.addEventListener("click", function (event) {
      event.preventDefault();
      self.clearModel();
    });

  };



  document.addEventListener("DOMContentLoaded", function () {
    var root = document.querySelector("[data-model-facet]");
    if (root && root.dataset.indexBase) new ModelFacet(root);
  });
})();
